<script>
  import { onDestroy, onMount, tick } from "svelte"
  import Moveable from "../Moveable.svelte"
  import PdfPage from "../pdf/PdfPage.svelte"
  import { loadPdfDocument } from "../pdf/pdfjs"
  import LoadingSpinner from "../widgets/LoadingSpinner.svelte"
  import { clampToPage, round1, toPoints } from "./coordinates"
  import FieldPalette from "./FieldPalette.svelte"
  import PlacementBox from "./PlacementBox.svelte"
  import PlacementToolbar from "./PlacementToolbar.svelte"

  export let customFieldsPath
  export let maxPlacements
  export let palette
  export let placements
  // async (placements) => {ok, errors?}; null disables autosave entirely.
  export let save = null
  export let url

  const CONTAINER_PADDING = 32
  const DEFAULT_HEIGHT_PT = 20
  const DEFAULT_WIDTH_PT = 180
  // Zoom may go past the initial fit, but a fresh load never starts blown up.
  const FIT_MAX_SCALE = 1.5
  const MAX_SCALE = 2
  const MIN_SCALE = 0.5
  const NUDGE_PT = 1
  const NUDGE_SHIFT_PT = 10
  const RENDER_DIRECTIONS = ["nw", "n", "ne", "w", "e", "sw", "s", "se"]
  const SAVE_DEBOUNCE_MS = 800
  const ZOOM_STEP = 0.25

  // cid is an ephemeral client id: the contract allows duplicate keys, so
  // keyed {#each} blocks and selection need something unique per box.
  // serialize() strips it back off before saving.
  let nextCid = 0

  let atLimitNotice = false
  let boxElements = {}
  let dirty = false
  let error = null
  let interacting = false
  let items = placements.map((placement) => ({ cid: nextCid++, ...placement }))
  let lastPointer = { x: 0, y: 0 }
  let moveableRef = null
  let pageElements = {}
  let pages = []
  let pdf = null
  let pendingSaveTimeout = null
  let saveErrors = []
  let saveLoopPromise = null
  let saveStatus = null
  let scale = 1
  let scrollContainer
  let selectedCid = null
  let submittingAfterSave = false
  let waitingToSubmit = false

  $: fieldsByKey = new Map(
    palette.flatMap((group) => group.fields.map((field) => [field.key, field]))
  )
  $: if (items.length < maxPlacements) atLimitNotice = false
  $: selected = items.find((item) => item.cid === selectedCid) ?? null
  $: selectedPage = selected ? pages.find((page) => page.number === selected.page) : null
  $: moveableTarget = selected ? (boxElements[selected.cid] ?? null) : null

  onMount(async () => {
    try {
      const loaded = await loadPdfDocument(url)
      pdf = loaded.pdf
      scale = fitScale(loaded.pages)
      pages = loaded.pages
    } catch (e) {
      error = "The PDF could not be loaded. Reload the page to try again."
      throw e
    }
  })

  // Flush a pending save when the surrounding form submits (Preview stamps
  // from the saved record) and when the page navigates away — the last edit
  // shouldn't be lost to the debounce window.
  onMount(() => {
    const form = scrollContainer.closest("form")
    const flushBeforeSubmit = async (event) => {
      if (submittingAfterSave || (!dirty && !saveLoopPromise)) return

      event.preventDefault()
      if (waitingToSubmit) return

      waitingToSubmit = true
      const submitter = event.submitter
      const saved = await flushSave()
      waitingToSubmit = false
      if (!saved) return

      submittingAfterSave = true
      form.requestSubmit(submitter ?? undefined)
      submittingAfterSave = false
    }
    form?.addEventListener("submit", flushBeforeSubmit)
    return () => form?.removeEventListener("submit", flushBeforeSubmit)
  })

  onDestroy(() => {
    if (pendingSaveTimeout) flushSave()
    pdf?.loadingTask.destroy()
  })

  function addPlacement({ field, pageNumber, pagePoint }) {
    if (items.length >= maxPlacements) {
      atLimitNotice = true
      return
    }
    const page = pages.find((candidate) => candidate.number === pageNumber)
    // Center the new box on the drop point (pagePoint is CSS px from the
    // page's top-left corner). Formatting keys (align, font_size) are left
    // off until deliberately set — absent means the renderer's default.
    const placement = clampToPage({
      page,
      placement: {
        height: DEFAULT_HEIGHT_PT,
        key: field.key,
        page: pageNumber,
        width: DEFAULT_WIDTH_PT,
        x: pagePoint.x / scale - DEFAULT_WIDTH_PT / 2,
        y: page.height - pagePoint.y / scale - DEFAULT_HEIGHT_PT / 2,
      },
    })
    const cid = nextCid++
    items = [...items, { cid, ...placement }]
    selectedCid = cid
    scheduleSave()
  }

  // Moveable mutates inline styles while dragging; on commit we read the
  // box's on-screen rect back, land it on whichever page its center sits
  // over (so boxes can be dragged across page boundaries), convert to
  // points, clamp, and let the placement re-derive the style — the box
  // self-corrects into the contract's grid.
  function commitSelectedElement() {
    const element = boxElements[selectedCid]
    const boxRect = element.getBoundingClientRect()
    const centerX = boxRect.left + boxRect.width / 2
    const centerY = boxRect.top + boxRect.height / 2

    let pageNumber = selected.page
    for (const [number, candidate] of Object.entries(pageElements)) {
      const candidateRect = candidate.getBoundingClientRect()
      if (
        centerX >= candidateRect.left &&
        centerX <= candidateRect.right &&
        centerY >= candidateRect.top &&
        centerY <= candidateRect.bottom
      ) {
        pageNumber = Number(number)
        break
      }
    }

    const page = pages.find((candidate) => candidate.number === pageNumber)
    const pageRect = pageElements[pageNumber].getBoundingClientRect()
    const rect = {
      height: Number.parseFloat(element.style.height),
      left: boxRect.left - pageRect.left,
      top: boxRect.top - pageRect.top,
      width: Number.parseFloat(element.style.width),
    }
    updateSelected(
      clampToPage({
        page,
        placement: { ...selected, page: pageNumber, ...toPoints({ page, rect, scale }) },
      })
    )
  }

  function deleteSelected() {
    items = items.filter((item) => item.cid !== selectedCid)
    selectedCid = null
    scheduleSave()
  }

  // A plain click on a box fires dragStart/dragEnd with no movement in
  // between; isDrag distinguishes that from a real drag so selecting a
  // placement never dirties the mapping.
  function endInteraction(e) {
    interacting = false
    if (e.isDrag) commitSelectedElement()
  }

  function fitScale(loaded) {
    const widest = Math.max(...loaded.map((page) => page.width))
    const available = scrollContainer.clientWidth - CONTAINER_PADDING
    return Math.min(FIT_MAX_SCALE, Math.max(MIN_SCALE, available / widest))
  }

  async function flushSave() {
    clearTimeout(pendingSaveTimeout)
    pendingSaveTimeout = null

    if (!save) return true
    if (saveLoopPromise) return saveLoopPromise
    if (!dirty) return true

    saveLoopPromise = saveUntilCurrent()
    try {
      return await saveLoopPromise
    } finally {
      saveLoopPromise = null
    }
  }

  // Keyboard/tap add: drop onto the center of the topmost page currently in
  // view, so palette clicks work without a pointer drag.
  function handleAdd({ field }) {
    const containerRect = scrollContainer.getBoundingClientRect()
    const visible = Object.entries(pageElements)
      .map(([number, element]) => ({
        number: Number(number),
        rect: element.getBoundingClientRect(),
      }))
      .filter(({ rect }) => rect.bottom > containerRect.top && rect.top < containerRect.bottom)
      .sort((a, b) => a.number - b.number)[0]
    if (!visible) return
    const visibleCenterY =
      (Math.max(visible.rect.top, containerRect.top) +
        Math.min(visible.rect.bottom, containerRect.bottom)) /
      2
    addPlacement({
      field,
      pageNumber: visible.number,
      pagePoint: { x: visible.rect.width / 2, y: visibleCenterY - visible.rect.top },
    })
  }

  function handleDrop({ clientX, clientY, field }) {
    const hit = Object.entries(pageElements).find(([, element]) => {
      const rect = element.getBoundingClientRect()
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      )
    })
    if (!hit) return
    const [number, element] = hit
    const rect = element.getBoundingClientRect()
    addPlacement({
      field,
      pageNumber: Number(number),
      pagePoint: { x: clientX - rect.left, y: clientY - rect.top },
    })
  }

  function handleKeydown(event) {
    if (!selected) return
    if (event.target?.closest?.("input, select, textarea, [contenteditable]")) return

    if (event.key === "Escape") {
      selectedCid = null
      return
    }
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault()
      deleteSelected()
      return
    }

    const nudge = event.shiftKey ? NUDGE_SHIFT_PT : NUDGE_PT
    const delta = {
      ArrowDown: [0, -nudge],
      ArrowLeft: [-nudge, 0],
      ArrowRight: [nudge, 0],
      ArrowUp: [0, nudge],
    }[event.key]
    if (!delta) return
    event.preventDefault()
    updateSelected(
      clampToPage({
        page: selectedPage,
        placement: {
          ...selected,
          x: round1(selected.x + delta[0]),
          y: round1(selected.y + delta[1]),
        },
      })
    )
  }

  function maybeDeselect(event) {
    // Page background clicks land on the render canvas; clicks in the scroll
    // container's own padding land on the container. Anything else (a box,
    // Moveable's handles) keeps the selection.
    if (event.target === scrollContainer || event.target.tagName === "CANVAS") selectedCid = null
  }

  // Moveable's scrollable plugin folds container scrolling into its drag
  // model but skips the repaint (gesto.scrollBy is called without a drag
  // dispatch), so the box and its handles would only catch up on the next
  // real pointer move. A synthetic mousemove at the cursor's last position
  // forces that repaint; queueMicrotask runs it after the plugin's own
  // scroll handler has folded the offset in, and the zero pointer delta
  // means it can't move anything on its own.
  function repaintAfterScroll() {
    queueMicrotask(() => {
      if (!interacting) return
      window.dispatchEvent(
        new MouseEvent("mousemove", { clientX: lastPointer.x, clientY: lastPointer.y })
      )
    })
  }

  function scheduleSave() {
    if (!save) return
    dirty = true
    clearTimeout(pendingSaveTimeout)
    saveStatus = null
    pendingSaveTimeout = setTimeout(flushSave, SAVE_DEBOUNCE_MS)
  }

  // An edit that lands during a failed save supersedes the failure (retry with
  // the fresh data instead of reporting it); a failure with no newer edit stays
  // dirty so the next flush retries.
  async function saveUntilCurrent() {
    while (dirty) {
      dirty = false
      saveErrors = []
      saveStatus = "saving"

      try {
        const response = await save(serialize())
        if (!response.ok) {
          if (dirty) continue
          dirty = true
          saveErrors = response.errors ?? ["The changes could not be saved."]
          saveStatus = "error"
          return false
        }
      } catch {
        if (dirty) continue
        dirty = true
        saveErrors = ["The changes could not be saved. Check your connection and try again."]
        saveStatus = "error"
        return false
      }
    }

    saveStatus = "saved"
    return true
  }

  function serialize() {
    return items.map(({ cid, ...placement }) => placement)
  }

  function setAlign(align) {
    updateSelected({ ...selected, align })
  }

  function setFontSize(fontSize) {
    updateSelected({ ...selected, font_size: fontSize })
  }

  function startInteraction(event) {
    interacting = true
    lastPointer = { x: event.clientX, y: event.clientY }
  }

  async function updateSelected(placement) {
    items = items.map((item) => (item.cid === selectedCid ? { ...item, ...placement } : item))
    scheduleSave()
    await tick()
    moveableRef?.updateRect()
  }

  async function zoomBy(step) {
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + step))
    await tick()
    moveableRef?.updateRect()
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="flex flex-col lg:flex-row overflow-hidden">
  <FieldPalette
    {customFieldsPath}
    {palette}
    on:add={(event) => handleAdd(event.detail)}
    on:drop={(event) => handleDrop(event.detail)}
  />

  <div class="flex flex-1 flex-col min-w-0">
    <PlacementToolbar
      {saveStatus}
      {scale}
      {selected}
      on:align={(event) => setAlign(event.detail)}
      on:delete={deleteSelected}
      on:fontsize={(event) => setFontSize(event.detail)}
      on:retry={flushSave}
      on:zoomin={() => zoomBy(ZOOM_STEP)}
      on:zoomout={() => zoomBy(-ZOOM_STEP)}
    />

    {#if saveErrors.length > 0}
      <div class="bg-red-50 border-b border-red-200 px-4 py-2 text-red-700 text-sm">
        {saveErrors.join(" ")}
      </div>
    {/if}

    {#if atLimitNotice}
      <div class="bg-amber-50 border-amber-200 border-b px-4 py-2 text-amber-800 text-sm">
        This document already has the maximum of {maxPlacements} placed fields. Delete one to add
        another.
      </div>
    {/if}

    <!-- Pointer-only by design: clicking empty space deselects; keyboard users deselect via the field list. -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      bind:this={scrollContainer}
      class="bg-slate-100 flex flex-col gap-6 max-h-[80vh] overflow-auto p-4"
      on:pointerdown={maybeDeselect}
      on:scroll={() => {
        if (interacting) {
          repaintAfterScroll()
        } else {
          moveableRef?.updateRect()
        }
      }}
    >
      {#if error}
        <p class="py-8 text-center text-red-500 text-sm">{error}</p>
      {:else if pages.length === 0}
        <div class="flex gap-2 items-center justify-center py-8 text-slate-600 text-sm">
          <LoadingSpinner classNames="h-4 w-4" />
          Loading PDF…
        </div>
      {:else}
        {#each pages as page (page.number)}
          <div
            bind:this={pageElements[page.number]}
            class="bg-white mx-auto relative shadow shrink-0"
            data-pdf-page={page.number}
            style="height: {page.height * scale}px; width: {page.width * scale}px;"
          >
            <PdfPage {page} {pdf} {scale} />

            {#each items.filter((item) => item.page === page.number) as item (item.cid)}
              <PlacementBox
                bind:element={boxElements[item.cid]}
                field={fieldsByKey.get(item.key)}
                {page}
                placement={item}
                {scale}
                selected={item.cid === selectedCid}
                on:select={() => (selectedCid = item.cid)}
              />
            {/each}

            {#if selected?.page === page.number && moveableTarget}
              <Moveable
                bind:this={moveableRef}
                draggable
                onDrag={(e) => {
                  lastPointer = { x: e.clientX, y: e.clientY }
                  e.target.style.left = `${e.left}px`
                  e.target.style.top = `${e.top}px`
                }}
                onDragEnd={endInteraction}
                onDragStart={startInteraction}
                onResize={(e) => {
                  lastPointer = { x: e.clientX, y: e.clientY }
                  e.target.style.height = `${e.height}px`
                  e.target.style.left = `${e.drag.left}px`
                  e.target.style.top = `${e.drag.top}px`
                  e.target.style.width = `${e.width}px`
                }}
                onResizeEnd={endInteraction}
                onResizeStart={startInteraction}
                onScroll={(e) => {
                  // Edge auto-scroll while dragging: moveable reports the
                  // direction, we advance the container.
                  e.scrollContainer.scrollLeft += e.direction[0] * 10
                  e.scrollContainer.scrollTop += e.direction[1] * 10
                }}
                origin={false}
                renderDirections={RENDER_DIRECTIONS}
                resizable
                rotatable={false}
                scrollOptions={{
                  checkScrollEvent: true,
                  container: () => scrollContainer,
                  threshold: 30,
                }}
                scrollable
                target={moveableTarget}
                throttleDrag={1}
                throttleResize={1}
              />
            {/if}
          </div>
        {/each}
      {/if}
    </div>
  </div>
</div>
